import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { leagueAPI, leaguesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Settings, Palette, Users, BookOpen, UserCircle,
  Info, LogOut, Moon, Sun, Monitor, Plus, ChevronRight, Copy,
  ShieldCheck, BarChart3, Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import NotificationSettings from '../components/NotificationSettings';
import LeagueDiscovery from '../components/LeagueDiscovery';
import ConfirmDialog from '../components/ConfirmDialog';
import GuestUpgradeCard from '../components/GuestUpgradeCard';
import SecurityCenter from './SecurityCenter';
import RewardedAdButton from '../components/RewardedAdButton';
import AdBanner from '../components/AdBanner';

// Configurable limits
const MAX_LEAGUE_NAME_LENGTH = 30;
const MAX_TEAM_NAME_LENGTH = 25;
const MIN_STARTING_BUDGET = 100;
const MAX_STARTING_BUDGET = 100000;
// 6 categories × 3 people = 18 (down from 21 in v5.7.23 when fictional
// characters were retired from the People catalogue).
const MAX_TOTAL_ENTITY_SLOTS = 18;

const STAGE_STYLES = [
  { id: 'colosseum', label: 'Rome' },
  { id: 'theater', label: 'Shakespeare' },
  { id: 'studio', label: 'Hollywood' },
];

// Static components defined OUTSIDE render to prevent re-mount on state change
function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
    </div>
  );
}

function SettingsRow({ label, value, onClick, right, testId, expanded, destructive }) {
  return (
    <div
      className={`flex items-center justify-between px-4 py-3 border-b border-border/40 last:border-0 ${onClick ? 'cursor-pointer hover:bg-secondary/50 active:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-expanded={expanded}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      data-testid={testId}
    >
      <span className={`text-sm ${destructive ? 'text-destructive font-medium' : ''}`}>{label}</span>
      {right || (value && <span className="text-sm text-muted-foreground">{value}</span>)}
      {onClick && !right && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
    </div>
  );
}

export default function MoreTab({ selectedLeagueId, onNotifications, onCommunity, onPremium, onScoringRules, onProfile, onAbout, onLeagueEntitySelect, onUserStats, onWatchlist }) {
  const { user, logout, refreshUser } = useAuth();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'auto');
  const [stageStyle, setStageStyle] = useState(localStorage.getItem('stage_style') || 'theater');
  const [showCreateLeague, setShowCreateLeague] = useState(false);
  const [showJoinLeague, setShowJoinLeague] = useState(false);
  const [leagueName, setLeagueName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  // 2026-08-15 (pool editing) — "My Leagues" management rows: live
  // "custom pool: N" chips + owner-only pool editor entry. Refreshed
  // whenever the selector saves (fws:league-pool-updated).
  const [myLeagues, setMyLeagues] = useState([]);
  const [showMyLeagues, setShowMyLeagues] = useState(false);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchLeagues = () =>
      leagueAPI.list()
        .then((r) => { if (!cancelled) setMyLeagues(r.data?.leagues || []); })
        .catch(() => {});
    fetchLeagues();
    window.addEventListener('fws:league-pool-updated', fetchLeagues);
    return () => { cancelled = true; window.removeEventListener('fws:league-pool-updated', fetchLeagues); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // v5.7.89 — League cap awareness. Fetch once on mount and re-fetch
  // after every successful create/join so the small "X of 10" hint
  // under both buttons reflects reality without a hard page refresh.
  const [capInfo, setCapInfo] = useState(null);
  const refreshCapInfo = async () => {
    try {
      const res = await leaguesAPI.capInfo();
      setCapInfo(res.data);
    } catch (e) {
      // Non-fatal — the create/join endpoints will surface 429 if hit.
    }
  };
  useEffect(() => { refreshCapInfo(); }, []);
  const [leagueSlots, setLeagueSlots] = useState({
    country_leader: 3, ceo: 3, actor: 3, athlete: 3, musician: 3, influencer: 3
  });
  const [startingBudget, setStartingBudget] = useState(1000);
  const [maxMembers, setMaxMembers] = useState(20);
  // v5.7.48 — league duration. `weeks` default 13 (one quarter).
  // `startDate` default is the next Monday 00:00 local-time which we
  // convert to UTC at submit. Showing the auto-computed "scoring starts"
  // sentence gives the user a clear deadline for finalising their team.
  const [leagueWeeks, setLeagueWeeks] = useState(13);

  // v5.7.98 — Separate STRING state for each numeric input so the user
  // can fully clear the field while typing. The numeric state above is
  // still the source of truth on submit; the text state mirrors it on
  // mount and on blur (after clamping). This is the standard form
  // pattern: free-typing in between, clamp + commit on blur.
  const [startingBudgetText, setStartingBudgetText] = useState('1000');
  const [maxMembersText, setMaxMembersText] = useState('20');
  const [leagueWeeksText, setLeagueWeeksText] = useState('13');

  // v5.7.98 — Generic clamp-on-blur handler. Reads the current text
  // value, parses it as int. Empty / NaN → falls back to `dflt`.
  // Out of range → snaps to nearest bound. Updates BOTH the numeric
  // state (used on submit) AND the text state (re-syncs the input
  // display to the clamped value).
  const _commitNumber = (text, min, max, dflt, setNum, setText) => {
    const trimmed = (text ?? '').trim();
    let n = trimmed === '' ? NaN : parseInt(trimmed, 10);
    if (!Number.isFinite(n)) n = dflt;
    n = Math.min(max, Math.max(min, n));
    setNum(n);
    setText(String(n));
  };
  // v5.7.58 — Flip semantics: `isPrivate` controls whether the league is
  // INVITE-ONLY (true → league_type='private', no Discover listing) or
  // PUBLIC (false → league_type='public', shows in Discover). Default
  // is public so new leagues are discoverable by default and the user
  // only has to opt-in to invite-only behaviour.
  const [isPrivate, setIsPrivate] = useState(false);
  const [leagueStartDate, setLeagueStartDate] = useState(() => {
    // Compute next Monday 00:00 in UTC and format as 'YYYY-MM-DDTHH:mm'
    const ref = new Date();
    const days = ((7 - ref.getUTCDay()) % 7) || 7;  // Sun=0 -> 1, Sat=6 -> 2
    const next = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate() + days, 0, 0, 0));
    return next.toISOString().slice(0, 16);  // datetime-local format
  });
  const [appVersion, setAppVersion] = useState('');
  // v5.7.56 — "Sign out of all devices" confirmation flag.
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);
  // v5.7.80 — Security Center (MFA + Active Sessions) sub-screen.
  const [showSecurity, setShowSecurity] = useState(false);
  // Cross-platform Restore Purchases (2026-07) — surfaces the same
  // `/billing/restore` action from PremiumPage in the Settings list so
  // users who bought Premium on mobile can rebuild their web ad-free
  // entitlement without hunting through the upgrade flow.
  const [restoring, setRestoring] = useState(false);
  // 2026-07 — subtle entitlement-status line under the Restore row.
  // Reassures users their purchase is registered BEFORE they tap Restore
  // (cuts "did my payment go through?" support tickets). Sources
  // `/billing/entitlements` — same view the auth doc / mobile use.
  const [entitlements, setEntitlements] = useState(null);
  const leagueInputRef = useRef(null);

  // v5.7.98 — `<input type=datetime-local>` min attribute. Formatted in
  // the user's LOCAL timezone (NOT UTC) — the browser's datetime-local
  // input compares against the local clock. Recomputed once per render
  // (mount + state change), which is granular enough that a user can't
  // realistically pick a moment that crosses the threshold between
  // render and submit. The handleCreateLeague guard provides a 1-min
  // slack for that edge case anyway.
  const nowLocalDateTime = useMemo(() => {
    const d = new Date();
    // Subtract the timezone offset to get the local-wall-clock ISO,
    // then trim to the YYYY-MM-DDTHH:mm shape datetime-local accepts.
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 16);
  // Re-derive on every key dependency change so a long-open dialog
  // doesn't let the past sneak past the bar.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateLeague]);

  useEffect(() => {
    let cancelled = false;
    api.get('/app/info').then(res => {
      if (!cancelled) setAppVersion(res.data?.version || '');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // 2026-07 — fetch entitlement snapshot for the status line under the
  // Restore Purchases row. Re-runs when premium flips (e.g. after a
  // successful restore) so the line updates without a manual reload.
  // UX audit P1 — handleRestore already sets entitlements from its own
  // response; skip the redundant follow-up fetch it triggers.
  const skipNextEntitlementsFetch = useRef(false);
  useEffect(() => {
    if (!user) return; // pre-auth mount — skip (avoids 401 console noise)
    if (skipNextEntitlementsFetch.current) {
      skipNextEntitlementsFetch.current = false;
      return;
    }
    let cancelled = false;
    api.get('/billing/entitlements')
      .then((res) => { if (!cancelled) setEntitlements(res.data || null); })
      .catch(() => { /* silent — status line just hides */ });
    return () => { cancelled = true; };
    // Narrow deps ON PURPOSE: only an identity/premium flip should refetch;
    // `user` object identity churns on every /auth/me refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.premium]);

  // Human-readable status: "Ad-free · owned since Mar 2026 · via Apple"
  // or "No purchases yet". `admin_grant` (comp/reviewer/gift) intentionally
  // omits the "via …" suffix — it's not a customer purchase channel.
  const entitlementStatus = useMemo(() => {
    if (!entitlements) return null;
    if (!entitlements.premium) return 'No purchases yet';
    const parts = ['Ad-free'];
    const since = entitlements.premium_since;
    if (since) {
      const d = new Date(since);
      if (!Number.isNaN(d.getTime())) {
        const label = d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
        parts.push(`owned since ${label}`);
      }
    }
    const sourceLabel = {
      stripe: 'web',
      apple: 'Apple',
      google: 'Google Play',
    }[entitlements.premium_source];
    if (sourceLabel) parts.push(`via ${sourceLabel}`);
    else if (parts.length === 1) parts.push('active');  // premium, no date, no source
    return parts.join(' · ');
  }, [entitlements]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    if (newTheme === 'dark') document.documentElement.classList.add('dark');
    else if (newTheme === 'light') document.documentElement.classList.remove('dark');
    else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    }
    // Notify App-level listener so it knows the saved theme has changed
    // (lets the OS-change handler re-evaluate against the new selection).
    window.dispatchEvent(new Event('theme:changed'));
  };

  // Cross-platform Restore Purchases (2026-07) — mirrors PremiumPage
  // `handleRestore` so users who bought Premium on mobile can rebuild
  // ad-free from Settings without opening the upgrade flow.
  const handleRestore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      const r = await api.post('/billing/restore');
      const restored = r.data?.restored || [];
      if (r.data?.entitlements) {
        setEntitlements(r.data.entitlements);
        skipNextEntitlementsFetch.current = true;
      }
      if (r.data?.entitlements?.premium || restored.length) {
        await refreshUser?.();
        toast.success(
          restored.length
            ? 'Purchases restored — you\'re ad-free!'
            : 'Your purchases are already active.'
        );
      } else {
        toast.info('No previous purchases found for this account.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed — please try again.');
    } finally { setRestoring(false); }
  };

  const handleCreateLeague = async () => {
    if (!leagueName.trim()) { toast.error('Enter a league name'); return; }
    if (totalEntitySlots < 1) { toast.error('Need at least 1 person slot'); return; }
    if (totalEntitySlots > MAX_TOTAL_ENTITY_SLOTS) { toast.error(`Maximum ${MAX_TOTAL_ENTITY_SLOTS} total people slots`); return; }

    // v5.7.98 — Reject past start dates client-side. The backend also
    // validates but a fast, friendly toast saves a round-trip and is
    // clearer than a generic 400. Anything earlier than 1 minute ago
    // is rejected — 1 min slack avoids the user-typed-it-perfectly-but-
    // clicked-Submit-a-tick-later edge case.
    if (leagueStartDate) {
      const startMs = new Date(leagueStartDate).getTime();
      if (Number.isFinite(startMs) && startMs < Date.now() - 60_000) {
        toast.error('Start date must be in the future');
        return;
      }
    }

    // v5.7.98 — Make sure any half-typed number input commits before
    // submit. The blur handler only fires when the user TAB-s out;
    // pressing Enter to submit while focus is still in a number field
    // would otherwise send the unclamped value (or NaN).
    _commitNumber(startingBudgetText, MIN_STARTING_BUDGET, MAX_STARTING_BUDGET, 1000, setStartingBudget, setStartingBudgetText);
    _commitNumber(maxMembersText, 2, 20, 20, setMaxMembers, setMaxMembersText);
    _commitNumber(leagueWeeksText, 1, 52, 13, setLeagueWeeks, setLeagueWeeksText);

    setCreating(true);
    try {
      const allowedCategories = Object.entries(leagueSlots).filter(([_, v]) => v > 0).map(([k]) => k);
      const maxPerCat = Math.max(...Object.values(leagueSlots).filter(v => v > 0));
      
      const res = await leagueAPI.create({
        name: leagueName.slice(0, MAX_LEAGUE_NAME_LENGTH),
        // v5.7.58 — `isPrivate` checkbox controls whether the league
        // is invite-code-only (private) or appears in Discover (public).
        // Default unchecked = public.
        league_type: isPrivate ? 'private' : 'public',
        max_members: Math.min(Math.max(maxMembers, 1), 20),
        starting_budget: startingBudget,
        // v5.7.48 — league duration. start_date is a local datetime-local
        // value; convert to ISO with timezone so the backend stores it
        // correctly in UTC.
        weeks: Math.min(Math.max(parseInt(leagueWeeks) || 13, 1), 52),
        start_date: leagueStartDate ? new Date(leagueStartDate).toISOString() : undefined,
        // 2026-08-15 — slot_config is accepted (and validated) at CREATE
        // time, so the roster shape is atomic with the league itself.
        slot_config: leagueSlots,
      });
      
      // Follow-up update for the legacy allowed_categories/required_per_category
      // keys (not part of the create contract). slot_config already landed at
      // create, so a failure here is cosmetic — but surface it instead of
      // silently swallowing (testing-agent code review 2026-08-15).
      if (res.data.league?.id) {
        try {
          await leagueAPI.update(res.data.league.id, {
            allowed_categories: allowedCategories,
            required_per_category: maxPerCat,
            slot_config: leagueSlots,
            starting_budget: startingBudget,
          });
        } catch (updateErr) {
          console.warn('League created but settings update failed:', updateErr);
          toast.error('League created, but category settings may not have fully saved — check the league before inviting.');
        }
      }
      
      toast.success('League created! Now select which people are available.');
      setCopiedCode(res.data.invite_code);
      setLeagueName('');
      // v5.7.89 — Refresh the X-of-10 cap badge after a successful create.
      refreshCapInfo();
      // iteration_179 — membership changed: refetch My Leagues so the new
      // league shows when the user tabs back (was stale until a reload).
      leagueAPI.list().then((r) => setMyLeagues(r.data?.leagues || [])).catch(() => {});
      // Navigate to entity selector
      if (onLeagueEntitySelect && res.data.league?.id) {
        onLeagueEntitySelect(res.data.league.id, leagueSlots, maxMembers);
      }
      // Don't close form — keep it open so user can see and copy the code
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create league');
    } finally { setCreating(false); }
  };

  const handleJoinLeague = async () => {
    if (!inviteCode.trim()) return;
    try {
      const res = await leagueAPI.join({ invite_code: inviteCode });
      toast.success(`Joined ${res.data.league_name}!`);
      setShowJoinLeague(false);
      setInviteCode('');
      // v5.7.89 — Refresh the X-of-10 cap badge after a successful join.
      refreshCapInfo();
      // 2026-08-15 (testing-agent) — membership changed: refresh the
      // My Leagues rows so the joined league (+ pool chip) shows sans reload.
      leagueAPI.list().then((r) => setMyLeagues(r.data?.leagues || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateSlot = (key, delta) => {
    setLeagueSlots(prev => {
      const newVal = Math.max(0, prev[key] + delta);
      const newSlots = { ...prev, [key]: newVal };
      const total = Object.values(newSlots).reduce((a, b) => a + b, 0);
      if (total > MAX_TOTAL_ENTITY_SLOTS) return prev; // Don't exceed max total
      return newSlots;
    });
  };
  const totalEntitySlots = Object.values(leagueSlots).reduce((a, b) => a + b, 0);

  // v5.7.80 — When the Security sub-screen is open, render it full-
  // bleed in place of the More tab. The internal ArrowLeft button calls
  // onBack which flips this state back to false.
  if (showSecurity) {
    return <SecurityCenter onBack={() => setShowSecurity(false)} />;
  }

  return (
    <div className="p-4" data-testid="more-tab">
      <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        <Settings className="h-5 w-5 inline mr-2" /> Settings & More
      </h2>

      <ScrollArea className="h-[calc(100vh-200px)]">
        {/* Appearance */}
        <div className="mb-6">
          <SectionHeader icon={Palette} title="Appearance" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <div className="px-4 py-3">
              <p className="text-sm mb-3">Theme</p>
              <div className="flex gap-2">
                {[{ id: 'light', icon: Sun, label: 'Light' }, { id: 'dark', icon: Moon, label: 'Dark' }, { id: 'auto', icon: Monitor, label: 'Auto' }].map(({ id, icon: Icon, label }) => (
                  <button key={id} onClick={() => handleThemeChange(id)} data-testid={`theme-${id}`}
                    className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border text-xs transition-colors ${theme === id ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-border'}`}>
                    <Icon className="h-4 w-4" />{label}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border/40">
              <p className="text-sm mb-3">Stage Style</p>
              <div className="space-y-2">
                {STAGE_STYLES.map(style => (
                  <button key={style.id} onClick={() => { setStageStyle(style.id); localStorage.setItem('stage_style', style.id); }}
                    className={`w-full flex items-center px-3 py-2 rounded-xl border text-sm transition-colors ${stageStyle === style.id ? 'bg-primary/10 border-primary/50' : 'bg-secondary/50 border-border'}`}>
                    {style.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* League Management */}
        <div className="mb-6">
          <SectionHeader icon={Users} title="League Management" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <SettingsRow
              label="Create League"
              right={capInfo && (
                <span
                  className={`text-[11px] ${capInfo.at_cap ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}
                  data-testid="create-league-cap-badge"
                >
                  {capInfo.used} of {capInfo.cap}
                </span>
              )}
              onClick={() => setShowCreateLeague(!showCreateLeague)}
              expanded={showCreateLeague}
            />
            {showCreateLeague && (
              <div className="px-4 py-3 space-y-3 bg-secondary/30" onClick={(e) => e.stopPropagation()}>
                {capInfo && capInfo.at_cap && (
                  <div className="text-[11px] text-red-500 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="create-league-cap-warning">
                    You've reached the {capInfo.cap}-league limit. Leave one before creating another (or have the owner remove you).
                  </div>
                )}
                {capInfo && !capInfo.at_cap && (
                  <p className="text-[10px] text-muted-foreground" data-testid="create-league-cap-hint">
                    Each player can be in up to {capInfo.cap} leagues at a time. You're in {capInfo.used} of {capInfo.cap}.
                  </p>
                )}
                <input
                  ref={leagueInputRef}
                  type="text"
                  placeholder="League name"
                  value={leagueName}
                  onChange={(e) => setLeagueName(e.target.value.slice(0, MAX_LEAGUE_NAME_LENGTH))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateLeague(); }}
                  maxLength={MAX_LEAGUE_NAME_LENGTH}
                  className="w-full flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="create-league-name-input"
                  autoFocus
                />
                <p className="text-[10px] text-muted-foreground text-right">{leagueName.length}/{MAX_LEAGUE_NAME_LENGTH}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground">Starting Budget (₣) ({MIN_STARTING_BUDGET} - {MAX_STARTING_BUDGET.toLocaleString()})</label>
                    <input type="number" value={startingBudgetText}
                      onChange={(e) => setStartingBudgetText(e.target.value)}
                      onBlur={() => _commitNumber(startingBudgetText, MIN_STARTING_BUDGET, MAX_STARTING_BUDGET, 1000, setStartingBudget, setStartingBudgetText)}
                      className="w-full flex h-9 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="create-league-budget-input" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground">Max Players (2-20)</label>
                    <input type="number" value={maxMembersText}
                      onChange={(e) => setMaxMembersText(e.target.value)}
                      onBlur={() => _commitNumber(maxMembersText, 2, 20, 20, setMaxMembers, setMaxMembersText)}
                      className="w-full flex h-9 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="create-league-max-input" />
                  </div>
                </div>

                {/* v5.7.48 — League duration (weeks + start date) */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground">League Length (weeks, 1-52)</label>
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={leagueWeeksText}
                      onChange={(e) => setLeagueWeeksText(e.target.value)}
                      onBlur={() => _commitNumber(leagueWeeksText, 1, 52, 13, setLeagueWeeks, setLeagueWeeksText)}
                      className="w-full flex h-9 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="create-league-weeks-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground">Start Date (UTC)</label>
                    <input
                      type="datetime-local"
                      value={leagueStartDate}
                      min={nowLocalDateTime}
                      onChange={(e) => setLeagueStartDate(e.target.value)}
                      className="w-full flex h-9 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="create-league-start-input"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug" data-testid="create-league-scoring-hint">
                  ⏱️ Scoring + ₣ payouts begin at{' '}
                  <span className="font-semibold text-foreground">
                    {leagueStartDate
                      ? new Date(leagueStartDate).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : 'the start date'}
                  </span>
                  . Anyone who hasn't finalized their team by then misses points
                  on events between the start and when they finalize. Set
                  it ≥48 h out to give people time to draft.
                </p>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2">People Slots: {totalEntitySlots}/{MAX_TOTAL_ENTITY_SLOTS} max</p>
                  <div className="space-y-1.5">
                    {[
                      { key: 'country_leader', label: 'Rulers' },
                      { key: 'ceo', label: 'CEOs' },
                      { key: 'actor', label: 'Actors' },
                      { key: 'athlete', label: 'Athletes' },
                      { key: 'musician', label: 'Artists' },
                      { key: 'influencer', label: 'Influencers' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-xs">{label}</span>
                        <div className="flex items-center gap-2">
                          <button className="h-6 w-6 rounded bg-background border text-xs font-bold" onClick={() => updateSlot(key, -1)} data-testid={`create-league-slot-minus-${key}`}>-</button>
                          <span className="text-xs font-bold tabular-nums w-5 text-center" data-testid={`create-league-slot-count-${key}`}>{leagueSlots[key]}</span>
                          <button className="h-6 w-6 rounded bg-background border text-xs font-bold" onClick={() => updateSlot(key, 1)} data-testid={`create-league-slot-plus-${key}`}>+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* v5.7.58 — Private league toggle (flipped from
                    "Public" in v5.7.57). When checked, the league is
                    invite-code-only — does NOT show up in Discover.
                    When unchecked (default), the league is public:
                    anyone can find and join it from Discover. */}
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--stage-ember))]"
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                    data-testid="create-league-private-checkbox"
                  />
                  <div>
                    <p className="text-xs font-semibold">Private league</p>
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      {isPrivate
                        ? 'Invite-code only. Won\'t appear in Discover — only people you share the code with can join.'
                        : 'Public. Anyone can find and join this league from the Discover panel — invite code optional.'}
                    </p>
                  </div>
                </label>
                <Button className="w-full" onClick={handleCreateLeague} disabled={creating} data-testid="create-league-confirm-button">
                  <Plus className="h-4 w-4 mr-2" /> {creating ? 'Creating...' : 'Select League People'}
                </Button>
                {copiedCode && (
                  <div className="flex items-center gap-2 bg-card p-2 rounded-lg border">
                    <span className="text-xs text-muted-foreground">Code:</span>
                    <code className="text-sm font-mono font-bold">{copiedCode}</code>
                    <button onClick={() => { try { navigator.clipboard.writeText(copiedCode); toast.success('Copied!'); } catch(e) { toast.info('Code: ' + copiedCode); }; }} aria-label="Copy invite code" className="ml-auto"><Copy className="h-3.5 w-3.5 text-muted-foreground" /></button>
                  </div>
                )}
              </div>
            )}
            <SettingsRow
              label="Join League"
              right={capInfo && (
                <span
                  className={`text-[11px] ${capInfo.at_cap ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}
                  data-testid="join-league-cap-badge"
                >
                  {capInfo.used} of {capInfo.cap}
                </span>
              )}
              onClick={() => setShowJoinLeague(!showJoinLeague)}
              expanded={showJoinLeague}
            />
            {/* Show last created league code even when create form is closed */}
            {copiedCode && !showCreateLeague && (
              <div className="px-4 py-3 bg-green-500/5 border-b border-border/40">
                <p className="text-xs text-muted-foreground mb-1">Last created league code:</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono font-bold text-primary">{copiedCode}</code>
                  <button onClick={() => { try { navigator.clipboard.writeText(copiedCode); toast.success('Copied!'); } catch(e) { toast.info('Code: ' + copiedCode); }; }} aria-label="Copy invite code">
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
            {showJoinLeague && (
              <div className="px-4 py-3 space-y-2 bg-secondary/30">
                {capInfo && capInfo.at_cap && (
                  <div className="text-[11px] text-red-500 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="join-league-cap-warning">
                    You've reached the {capInfo.cap}-league limit. Leave one before joining another.
                  </div>
                )}
                {capInfo && !capInfo.at_cap && (
                  <p className="text-[10px] text-muted-foreground" data-testid="join-league-cap-hint">
                    Each player can be in up to {capInfo.cap} leagues at a time. You're in {capInfo.used} of {capInfo.cap}.
                  </p>
                )}
                <input
                  type="text"
                  placeholder="Invite code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleJoinLeague(); }}
                  className="w-full flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="join-league-code-input"
                />
                <Button className="w-full" onClick={handleJoinLeague} data-testid="join-league-submit-button">Join League</Button>
              </div>
            )}

            {/* 2026-08-15 (pool editing) — My Leagues: pool badges + owner
                pool-editor entry (pre-start only; server enforces 409). */}
            <SettingsRow
              label="My Leagues"
              value={String(myLeagues.filter((l) => l.league_type !== 'global').length || '')}
              onClick={() => setShowMyLeagues(!showMyLeagues)}
              expanded={showMyLeagues}
              testId="more-my-leagues-row"
            />
            {showMyLeagues && (
              <div className="px-4 py-3 space-y-2 bg-secondary/30" data-testid="my-leagues-list">
                {myLeagues.filter((l) => l.league_type !== 'global').length === 0 && (
                  <p className="text-[11px] text-muted-foreground">You're not in any custom leagues yet.</p>
                )}
                {myLeagues.filter((l) => l.league_type !== 'global').map((lg) => {
                  const isOwner = lg.owner_id === user?.id;
                  const started = lg.start_date && new Date(lg.start_date) < new Date();
                  return (
                    <div key={lg.id} className="bg-card border border-border/40 rounded-lg p-2.5" data-testid={`my-league-${lg.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate">{lg.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {lg.member_count}/{lg.max_members || '∞'} joined · {lg.league_type}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {lg.pool_size > 0 && (
                            <span
                              className="text-[9px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-300 border border-sky-500/30 font-semibold"
                              data-testid={`my-league-pool-badge-${lg.id}`}
                            >
                              Custom pool: {lg.pool_size} people
                            </span>
                          )}
                          {isOwner && (
                            started ? (
                              <span className="text-[9px] text-muted-foreground italic" data-testid={`my-league-pool-locked-${lg.id}`}>
                                Pool locked (league started)
                              </span>
                            ) : (
                              <Button
                                size="sm" variant="outline" className="h-6 text-[10px]"
                                onClick={() => onLeagueEntitySelect(lg.id, null, lg.max_members)}
                                data-testid={`my-league-edit-pool-${lg.id}`}
                              >
                                Edit People Pool
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* League Discovery */}
        <div className="mb-6">
          <SectionHeader icon={Info} title="Discover Leagues" />
          <div className="bg-card border border-border/60 rounded-2xl p-4 min-h-[60px]">
            <LeagueDiscovery />
          </div>
        </div>

        {/* Guest → full account upgrade (guest sessions only) */}
        <GuestUpgradeCard />

        {/* Premium */}
        <div className="mb-6">
          <SectionHeader icon={Users} title="Premium" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <SettingsRow label={user?.premium ? "Premium Active" : "Upgrade to Premium"} value={user?.premium ? "Ad-free" : "$4.99/mo"} onClick={onPremium} />
            <SettingsRow
              label="Restore Purchases"
              onClick={handleRestore}
              right={
                <span className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {restoring ? 'Restoring…' : 'Bought on mobile? Sync here'}
                  </span>
                  <Unlock className="h-4 w-4 text-muted-foreground" />
                </span>
              }
              testId="more-restore-purchases-row"
            />
            {entitlementStatus && (
              <div
                className="px-4 py-2 text-[11px] text-muted-foreground bg-secondary/20 border-b border-border/40 last:border-0"
                data-testid="more-entitlement-status"
              >
                {entitlementStatus}
              </div>
            )}
            <RewardedAdButton leagueId={selectedLeagueId} />
          </div>
        </div>

        {/* Notifications */}
        <div className="mb-6"><NotificationSettings /></div>

        {/* Community */}
        <div className="mb-6">
          <SectionHeader icon={Users} title="Community" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <SettingsRow label="Global Chat" value="Message board" onClick={onCommunity} testId="more-community" />
            <SettingsRow label="Notifications" value="View all" onClick={onNotifications} testId="more-notifications" />
          </div>
        </div>

        {/* Library (parity with mobile) */}
        <div className="mb-6">
          <SectionHeader icon={BarChart3} title="Library" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <SettingsRow label="My Stats" value="Performance dashboard" onClick={onUserStats} />
            <SettingsRow label="Watchlist" value="People you're tracking" onClick={onWatchlist} testId="more-watchlist-row" />
          </div>
        </div>

        {/* Rules */}
        <div className="mb-6">
          <SectionHeader icon={BookOpen} title="How to Play & Scoring Rules" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <SettingsRow label="View How to Play & Scoring Rules" onClick={onScoringRules} />
          </div>
        </div>

        {/* Account */}
        <div className="mb-6">
          <SectionHeader icon={UserCircle} title="Account" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <SettingsRow label="My Profile" value="Gaming record" onClick={onProfile} testId="more-profile" />
            <SettingsRow label="Username" value={user?.username || 'Guest'} />
            <SettingsRow label="Email" value={user?.email || 'N/A'} />
            {/* v5.7.80 — Security Center: MFA enrollment + Active Sessions list. */}
            <SettingsRow
              label="Security"
              value="2FA & active devices"
              onClick={() => setShowSecurity(true)}
              right={<ShieldCheck className="h-4 w-4 text-muted-foreground" />}
            />
            <SettingsRow label="Sign Out" destructive onClick={logout} right={<LogOut className="h-4 w-4 text-destructive" />} testId="more-sign-out-btn" />
            {/* v5.7.56 — panic-button: revoke every active session for
                this account in one shot via POST /api/auth/logout-all. */}
            <SettingsRow
              label="Sign Out of All Devices"
              destructive
              onClick={() => setConfirmLogoutAll(true)}
              right={<LogOut className="h-4 w-4 text-destructive" />}
            />
          </div>
        </div>

        {/* Legal */}
        <div className="mb-6">
          <SectionHeader icon={Info} title="Legal & Compliance" />
          {/* 2026-08-15 — action rows: takedown ("remove me") + inclusion
              ("add a person") request forms, both public/no-auth pages. */}
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden mb-3">
            <SettingsRow
              label="Takedown Request"
              value="Remove a person"
              onClick={() => { window.location.href = '/legal/takedown'; }}
              testId="more-takedown-row"
            />
            <SettingsRow
              label="Inclusion Request"
              value="Add a person"
              onClick={() => { window.location.href = '/legal/inclusion'; }}
              testId="more-inclusion-row"
            />
          </div>
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden p-4">
            {/* v6.1.17.4 — External legal documents.  Privacy Policy + Terms of Service
                are hosted on the marketing site and must open in a new tab (external
                policy pages, not in-app webview content). */}
            <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4 pb-4 border-b border-border/40 text-sm" data-testid="more-legal-policy-links">
              <a
                href="https://fantasyworldstar.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                data-testid="more-privacy-link"
              >
                Privacy Policy
              </a>
              <a
                href="https://fantasyworldstar.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                data-testid="more-terms-link"
              >
                Terms of Service
              </a>
            </div>
            <div className="space-y-3 text-xs text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground">Fantasy Disclaimer:</strong> Fantasy World Star is a fantasy entertainment game based on publicly available information. It is not affiliated with, endorsed by, or sponsored by any person or organization shown.</p>
              <p><strong className="text-foreground">Scoring:</strong> All scoring and pricing is derived exclusively from objective, verifiable public events and public-attention data. Scores are not opinions, ratings of character, or endorsements — and we use no subjective allegations about any person.</p>
              <p><strong className="text-foreground">AI Artwork:</strong> Portraits for athletes, musicians, and actors are AI-generated stylized illustrations — not photographs and not real likenesses. Some public figures use attributed Wikimedia Commons photos under CC BY-SA 4.0 — browse the full <a href="/credits" className="underline">photo credits</a>. We do not display AI portraits of deceased people.</p>
              <p><strong className="text-foreground">Fantasy Dollars:</strong> ₣ are virtual in-game points with no cash value. They cannot be redeemed, withdrawn, or transferred, and confer no real-world value.</p>
              <p><strong className="text-foreground">No Gambling, No Prizes:</strong> This platform has NO platform-run cash prizes, no entry fees, no sweepstakes, and no gambling mechanics. Premium only removes mandatory ads — it provides zero gameplay advantage. Commissioner-organised private leagues are entirely user-run and outside the platform's scope. The App is intended for adults 18+.</p>
              <p><strong className="text-foreground">Data Rights:</strong> We respect your data rights under GDPR/CCPA and similar U.S. state laws. You may access, delete, or opt-out of your data at any time via your profile settings or by emailing <a href="mailto:contact@gazumbagames.com" className="underline">contact@gazumbagames.com</a>.</p>
              <p><strong className="text-foreground">Person Opt-Out:</strong> Any real person represented in this game (or their representative) may request removal by submitting the <a href="/legal/takedown" className="underline">takedown form</a> or emailing <a href="mailto:contact@gazumbagames.com" className="underline">contact@gazumbagames.com</a>. Approved requests result in immediate removal from all public lists, scoring, and rosters.</p>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="mb-6">
          <SectionHeader icon={Info} title="About" />
          <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
            <SettingsRow label="About Fantasy World Star" value={appVersion ? `v${appVersion}` : ''} onClick={onAbout} />
          </div>
        </div>

        {/* Inline ad banner (non-premium only; provider-aware: Ezoic/AdSense/house) */}
        <div className="mb-2">
          <AdBanner onPremium={onPremium} />
        </div>
      </ScrollArea>

      {/* v5.7.56 — Sign Out of All Devices confirmation. Calls the
          /api/auth/logout-all endpoint (added in v5.7.55) which bumps
          the user's token_epoch_s and invalidates every active token. */}
      <ConfirmDialog
        open={confirmLogoutAll}
        title="Sign out of ALL devices?"
        description={
          'This will invalidate every active session for your account — including this one, your phone, ' +
          'and any other browser/tab. Use this if you think your account may have been accessed ' +
          'somewhere it shouldn\'t have been, or if you simply want to start fresh.'
        }
        confirmLabel={signingOutAll ? 'Signing out...' : 'Yes, sign out everywhere'}
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={async () => {
          if (signingOutAll) return;
          setSigningOutAll(true);
          try {
            await api.post('/auth/logout-all');
            toast.success('All sessions revoked. Redirecting to sign-in...');
            setConfirmLogoutAll(false);
            // Force a hard reload so React state, auth context, and the
            // (now-stale) cookie are all flushed cleanly.
            setTimeout(() => { window.location.href = '/'; }, 700);
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to sign out all devices.');
            setSigningOutAll(false);
          }
        }}
        onCancel={() => setConfirmLogoutAll(false)}
      />
    </div>
  );
}
